import {
    collectPathPopulateGeometry,
    offsetPointAlongGeometryInViewport,
    samplePointsAlongGeometryInViewportAsync,
    withMountedPathSvgAsync,
} from "./svgUtils";
import {
    startFrameBudget,
    yieldIfFrameExhausted,
} from "./cooperativeScheduler";

export interface PathPopulatePlacement {
    x: number;
    y: number;
    angle: number;
    pathIndex: number;
    pathPosition: number;
    globalPosition: number;
    objectIndex: number;
}

export type PathPopulatePlacementMode = "alternating" | "random";

export interface PathPopulatePlacementComputeInput {
    pathSvgContent: string;
    distance: number;
    distanceOffsetRandomness: number;
    gridEnabled: boolean;
    gridSize: number;
    objectCount: number;
    objectPlacementMode: PathPopulatePlacementMode;
    signal: AbortSignal;
    /** Optional progress callback; called between phases / per path segment. */
    onProgress?: (info: PathPopulateProgress) => void;
}

export type PathPopulateProgressPhase =
    | "mount"
    | "sample"
    | "dedupe"
    | "assign";

export interface PathPopulateProgress {
    phase: PathPopulateProgressPhase;
    processed: number;
    total: number;
}

export interface PathPopulateLengthEstimate {
    totalLengthPx: number;
    segmentCount: number;
}

/**
 * djb2-style fingerprint, paired with string length to make accidental collisions
 * between distinct path SVGs vanishingly unlikely while keeping keys tiny.
 */
export function fingerprintSvgContent(content: string): string {
    let h = 5381;
    for (let i = 0; i < content.length; i++) {
        h = Math.imul(h, 33) ^ content.charCodeAt(i);
    }
    return `${content.length}:${(h >>> 0).toString(36)}`;
}

/**
 * Deterministic uniform-ish [0, 1) from segment index + placement ordinal.
 * Shared between the placement pipeline and placement-time helpers so
 * previews, random path/instance color, scale randomness, and scatter all agree.
 */
export function hashPlacementToUnit(
    pathIndex: number,
    ordinal: number,
    salt: number,
): number {
    let h =
        Math.imul(pathIndex | 0, 0x9e3779b1) ^
        Math.imul(ordinal | 0, 0x85ebca6b) ^
        salt;
    h >>>= 0;
    h ^= h >>> 16;
    h = Math.imul(h, 0x7feb352d);
    h ^= h >>> 15;
    h = Math.imul(h, 0x846ca68b);
    h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
}

/** Stable pseudo-random #RRGGBB per placement (preview and export match). */
export function randomHexColorForPlacement(
    pathIndex: number,
    ordinal: number,
): string {
    const r = Math.floor(
        hashPlacementToUnit(pathIndex, ordinal, 0xa41f3c71) * 256,
    );
    const g = Math.floor(
        hashPlacementToUnit(pathIndex, ordinal, 0xb72d4e82) * 256,
    );
    const b = Math.floor(
        hashPlacementToUnit(pathIndex, ordinal, 0xc83e5f93) * 256,
    );
    const hex = (n: number) => n.toString(16).padStart(2, "0");
    return `#${hex(r)}${hex(g)}${hex(b)}`;
}

/** Deterministic index in `[0, paletteLength)` for per-instance palette picks. */
export function paletteColorIndexForPlacement(
    pathIndex: number,
    ordinal: number,
    paletteLength: number,
): number {
    const n = Math.max(1, paletteLength | 0);
    const u = hashPlacementToUnit(pathIndex, ordinal, 0xd94e6a04);
    return Math.min(n - 1, Math.floor(u * n));
}

function throwIfAborted(signal: AbortSignal): void {
    if (signal.aborted) {
        throw new DOMException("Aborted", "AbortError");
    }
}

interface PointWithPath {
    x: number;
    y: number;
    angle: number;
    position: number;
    pathIndex: number;
}

interface PointWithGlobal extends PointWithPath {
    globalPosition: number;
}

function assignObjectIndices(
    uniquePoints: PointWithGlobal[],
    objectCount: number,
    objectPlacementMode: PathPopulatePlacementMode,
): PathPopulatePlacement[] {
    const finalTotal = uniquePoints.length;
    let randomSeed = 0;
    const seededRandom = () => {
        randomSeed = (randomSeed * 9301 + 49297) % 233280;
        return randomSeed / 233280;
    };

    return uniquePoints.map((p, idx) => {
        let objectIndex: number;
        if (objectPlacementMode === "alternating") {
            objectIndex = idx % objectCount;
        } else {
            randomSeed = Math.floor(p.x * 1000 + p.y * 1000 + idx * 1000);
            objectIndex = Math.floor(seededRandom() * objectCount);
        }

        return {
            x: p.x,
            y: p.y,
            angle: p.angle,
            pathIndex: p.pathIndex,
            pathPosition: p.position,
            globalPosition: finalTotal > 1 ? idx / (finalTotal - 1) : 0,
            objectIndex,
        };
    });
}

/**
 * Cheap first pass: mount the path SVG once, sum `getTotalLength()` across all
 * geometry, and return segment count. Yields cooperatively when there are many
 * paths. Lets the UI show an estimate before running the heavy sampling.
 */
export async function estimatePathLengthAsync(
    pathSvgContent: string,
    signal: AbortSignal,
): Promise<PathPopulateLengthEstimate | null> {
    const result = await withMountedPathSvgAsync(
        pathSvgContent,
        async (root) => {
            const segments = collectPathPopulateGeometry(root);
            const budget = startFrameBudget();
            let sum = 0;
            for (let i = 0; i < segments.length; i++) {
                await yieldIfFrameExhausted(budget, signal);
                sum += segments[i].el.getTotalLength();
            }
            return { totalLengthPx: sum, segmentCount: segments.length };
        },
    );
    return result ?? null;
}

/**
 * Full placement resolution with cooperative scheduling so the tab stays responsive
 * on dense path SVGs. Uses a single frame budget shared across sampling + dedup
 * so the scheduler overhead stays proportional to wall-clock cost, not iteration count.
 */
export async function computePathPopulatePlacementsAsync(
    input: PathPopulatePlacementComputeInput,
): Promise<{ pathSegmentCount: number; placements: PathPopulatePlacement[] }> {
    const {
        pathSvgContent,
        distance,
        distanceOffsetRandomness,
        gridEnabled,
        gridSize,
        objectCount,
        objectPlacementMode,
        signal,
        onProgress,
    } = input;

    if (distance <= 0) {
        return { pathSegmentCount: 0, placements: [] };
    }

    const budget = startFrameBudget();

    onProgress?.({ phase: "mount", processed: 0, total: 1 });

    const mounted = await withMountedPathSvgAsync(
        pathSvgContent,
        async (root) => {
            const segments = collectPathPopulateGeometry(root);
            const collected: PointWithPath[] = [];

            for (let pathIndex = 0; pathIndex < segments.length; pathIndex++) {
                throwIfAborted(signal);
                onProgress?.({
                    phase: "sample",
                    processed: pathIndex,
                    total: segments.length,
                });

                const { el, isClosed } = segments[pathIndex];
                const sampledPoints =
                    await samplePointsAlongGeometryInViewportAsync(
                        el,
                        distance,
                        isClosed,
                        { signal, budget },
                    );

                for (let spi = 0; spi < sampledPoints.length; spi++) {
                    let p = sampledPoints[spi];
                    if (distanceOffsetRandomness > 0) {
                        const u = hashPlacementToUnit(
                            pathIndex,
                            spi,
                            0x2a7d0bad,
                        );
                        const offset = (u * 2 - 1) * distanceOffsetRandomness;
                        p = offsetPointAlongGeometryInViewport(
                            el,
                            p,
                            offset,
                            isClosed,
                        );
                    }
                    collected.push({
                        x: p.x,
                        y: p.y,
                        angle: p.angle,
                        position: p.position,
                        pathIndex,
                    });
                }
            }

            return { segmentCount: segments.length, collected };
        },
    );

    if (!mounted) {
        return { pathSegmentCount: 0, placements: [] };
    }

    const { segmentCount, collected: allPoints } = mounted;
    throwIfAborted(signal);

    if (allPoints.length === 0 || objectCount === 0) {
        return { pathSegmentCount: segmentCount, placements: [] };
    }

    let uniquePoints: PointWithGlobal[];

    if (gridEnabled && gridSize > 0) {
        const occupiedCells = new Set<string>();
        uniquePoints = [];
        for (let i = 0; i < allPoints.length; i++) {
            await yieldIfFrameExhausted(budget, signal);
            if (i % 8192 === 0) {
                onProgress?.({
                    phase: "dedupe",
                    processed: i,
                    total: allPoints.length,
                });
            }
            const src = allPoints[i];
            const snappedX = Math.round(src.x / gridSize) * gridSize;
            const snappedY = Math.round(src.y / gridSize) * gridSize;
            const cellKey = `${snappedX},${snappedY}`;
            if (!occupiedCells.has(cellKey)) {
                occupiedCells.add(cellKey);
                uniquePoints.push({
                    ...src,
                    x: snappedX,
                    y: snappedY,
                    globalPosition: 0,
                });
            }
        }
    } else {
        const epsilon = Math.max(0.1, distance * 0.1);
        const seenPositions = new Set<string>();
        uniquePoints = [];
        for (let i = 0; i < allPoints.length; i++) {
            await yieldIfFrameExhausted(budget, signal);
            if (i % 8192 === 0) {
                onProgress?.({
                    phase: "dedupe",
                    processed: i,
                    total: allPoints.length,
                });
            }
            const src = allPoints[i];
            const roundedX = Math.round(src.x / epsilon) * epsilon;
            const roundedY = Math.round(src.y / epsilon) * epsilon;
            const key = `${roundedX},${roundedY}`;
            if (!seenPositions.has(key)) {
                seenPositions.add(key);
                uniquePoints.push({ ...src, globalPosition: 0 });
            }
        }
    }

    throwIfAborted(signal);
    onProgress?.({
        phase: "assign",
        processed: 0,
        total: uniquePoints.length,
    });

    const finalTotal = uniquePoints.length;
    for (let i = 0; i < uniquePoints.length; i++) {
        await yieldIfFrameExhausted(budget, signal);
        uniquePoints[i].globalPosition =
            finalTotal > 1 ? i / (finalTotal - 1) : 0;
    }

    return {
        pathSegmentCount: segmentCount,
        placements: assignObjectIndices(
            uniquePoints,
            objectCount,
            objectPlacementMode,
        ),
    };
}
